import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { applicationsAPI, userAPI, portfolioAPI, jobAlertsAPI, dashboardAPI } from '../api/api';
import { useWorkerLang } from '../contexts/WorkerLanguageContext';
import LangToggle from '../components/LangToggle';
import { Button } from '../components/ui/button';
import AdminViewBanner from '../components/AdminViewBanner';
import { getPlanDisplayName, getPlanFeatures, WORKER_PLANS } from '../utils/planFeatures';
import { 
  CurrencyDollar,
  Bell,
  Gear,
  User,
  FileText,
  ChatCircle,
  MagnifyingGlass,
  ShieldCheck,
  Image,
  ChartBar
} from '@phosphor-icons/react';
import { toast } from '../hooks/use-toast';
import PlanComparisonModal from '../components/PlanComparisonModal';
import { BreadcrumbsInline } from '../components/Breadcrumbs';
import OverviewTab from '../components/worker-panel/OverviewTab';
import ApplicationsTab from '../components/worker-panel/ApplicationsTab';
import PortfolioTab from '../components/worker-panel/PortfolioTab';
import JobAlertsTab from '../components/worker-panel/JobAlertsTab';
import ProfileTab from '../components/worker-panel/ProfileTab';
import EarningsTab from '../components/worker-panel/EarningsTab';
import SecurityTab from '../components/worker-panel/SecurityTab';
import SettingsTab from '../components/worker-panel/SettingsTab';

const WorkerControlPanel = () => {
  const navigate = useNavigate();
  const { t } = useWorkerLang();
  const [activeTab, setActiveTab] = useState('overview');
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [loadingStats, setLoadingStats] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [currentPlan, setCurrentPlan] = useState('free_worker');
  const [showPlanComparison, setShowPlanComparison] = useState(false);
  
  // Dashboard stats from API
  const [dashboardStats, setDashboardStats] = useState({
    applied_jobs: 0,
    pending_applications: 0,
    active_jobs: 0,
    month_earnings: 0,
    rating: 0,
    reviews_count: 0
  });
  
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    phone: '',
    location: '',
    trade: '',
    experience: '',
    bio: '',
    hourlyRate: '',
    availability: ''
  });

  const [skills, setSkills] = useState([]);
  const [languagesSpoken, setLanguagesSpoken] = useState([]);
  
  const [settings, setSettings] = useState({
    email_notifications: true,
    sms_alerts: false,
    weekly_digest: true,
    application_updates: true,
    profile_visible: true,
    show_contact_public: true,
    allow_messages: true,
    show_availability: true
  });

  const [applications, setApplications] = useState([]);
  const [loadingApps, setLoadingApps] = useState(true);
  const [portfolioItems, setPortfolioItems] = useState([]);
  const [loadingPortfolio, setLoadingPortfolio] = useState(true);
  
  // Job Alerts state
  const [jobAlertPrefs, setJobAlertPrefs] = useState({
    enabled: false,
    skills: [],
    locations: [],
    min_budget: '',
    max_budget: '',
    job_types: [],
    frequency: 'instant'
  });
  const [loadingAlerts, setLoadingAlerts] = useState(true);
  const [savingAlerts, setSavingAlerts] = useState(false);
  const [newAlertSkill, setNewAlertSkill] = useState('');
  const [newAlertLocation, setNewAlertLocation] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login?type=worker');
      return;
    }
    
    // Fetch user profile
    const fetchProfile = async () => {
      try {
        setLoadingProfile(true);
        const response = await userAPI.getMe();
        const user = response.data?.user || response.data;
        // Set subscription plan
        if (user.subscription_plan && user.subscription_status === 'active') {
          setCurrentPlan(user.subscription_plan);
        } else {
          setCurrentPlan('free_worker'); // Default free plan
        }
        setProfileData({
          name: user.name || '',
          email: user.email || '',
          phone: user.phone || '',
          location: user.location || '',
          trade: user.skills?.[0] || '',
          experience: user.experience || '',
          bio: user.bio || '',
          hourlyRate: user.hourly_rate || '',
          availability: user.availability || ''
        });
        setSkills(user.skills || []);
        setLanguagesSpoken(Array.isArray(user.languages_spoken) ? user.languages_spoken : []);
      } catch (error) {
        console.error('Error fetching profile:', error);
      } finally {
        setLoadingProfile(false);
      }
    };
    
    // Fetch settings
    const fetchSettings = async () => {
      try {
        const response = await userAPI.getSettings();
        setSettings(response.data.settings || {});
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };
    
    // Fetch applications
    const fetchApplications = async () => {
      try {
        setLoadingApps(true);
        const response = await applicationsAPI.getMyApplications();
        setApplications(response.data.applications || []);
      } catch (error) {
        console.error('Error fetching applications:', error);
      } finally {
        setLoadingApps(false);
      }
    };
    
    // Fetch job alert preferences
    const fetchJobAlertPrefs = async () => {
      try {
        setLoadingAlerts(true);
        const response = await jobAlertsAPI.getPreferences();
        if (response.data.preferences) {
          setJobAlertPrefs({
            enabled: response.data.preferences.enabled || false,
            skills: response.data.preferences.skills || [],
            locations: response.data.preferences.locations || [],
            min_budget: response.data.preferences.min_budget || '',
            max_budget: response.data.preferences.max_budget || '',
            job_types: response.data.preferences.job_types || [],
            frequency: response.data.preferences.frequency || 'instant'
          });
        }
      } catch (error) {
        console.error('Error fetching job alert preferences:', error);
      } finally {
        setLoadingAlerts(false);
      }
    };
    
    // Fetch dashboard stats
    const fetchDashboardStats = async () => {
      try {
        setLoadingStats(true);
        const response = await dashboardAPI.getWorkerDashboard();
        if (response.data.success) {
          setDashboardStats(response.data.stats);
        }
      } catch (error) {
        console.error('Error fetching dashboard stats:', error);
      } finally {
        setLoadingStats(false);
      }
    };
    
    fetchProfile();
    fetchSettings();
    fetchApplications();
    fetchJobAlertPrefs();
    fetchDashboardStats();
  }, [navigate]);

  // Fetch portfolio items
  const fetchPortfolio = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    
    try {
      setLoadingPortfolio(true);
      // Get current user ID from token
      const meResponse = await userAPI.getMe();
      const userId = (meResponse.data?.user || meResponse.data)._id;
      const response = await portfolioAPI.getWorkerPortfolio(userId);
      setPortfolioItems(response.data.items || []);
    } catch (error) {
      console.error('Error fetching portfolio:', error);
    } finally {
      setLoadingPortfolio(false);
    }
  }, []);

  useEffect(() => {
    fetchPortfolio();
  }, [fetchPortfolio]);

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      await userAPI.updateProfile({
        name: profileData.name,
        phone: profileData.phone,
        location: profileData.location,
        bio: profileData.bio,
        hourly_rate: profileData.hourlyRate,
        experience: profileData.experience,
        availability: profileData.availability,
        skills: skills,
        languages_spoken: languagesSpoken
      });
      toast({
        title: 'Profile Updated',
        description: 'Your profile has been saved successfully.',
      });
    } catch (error) {
      console.error('Error saving profile:', error);
      toast({
        title: 'Error',
        description: 'Failed to save profile. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setSavingProfile(false);
    }
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      await userAPI.updateSettings(settings);
      toast({
        title: 'Settings Saved',
        description: 'Your preferences have been updated.',
      });
    } catch (error) {
      console.error('Error saving settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to save settings. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setSavingSettings(false);
    }
  };

  const handleSaveJobAlerts = async () => {
    setSavingAlerts(true);
    try {
      await jobAlertsAPI.savePreferences({
        ...jobAlertPrefs,
        min_budget: jobAlertPrefs.min_budget ? parseFloat(jobAlertPrefs.min_budget) : null,
        max_budget: jobAlertPrefs.max_budget ? parseFloat(jobAlertPrefs.max_budget) : null
      });
      toast({
        title: 'Job Alerts Saved',
        description: jobAlertPrefs.enabled 
          ? 'You will receive alerts for matching jobs.' 
          : 'Job alerts have been disabled.',
      });
    } catch (error) {
      console.error('Error saving job alerts:', error);
      toast({
        title: 'Error',
        description: 'Failed to save job alert preferences.',
        variant: 'destructive'
      });
    } finally {
      setSavingAlerts(false);
    }
  };

  const handleSendTestAlert = async () => {
    try {
      await jobAlertsAPI.sendTestAlert();
      toast({
        title: 'Test Alert Sent',
        description: 'Check your email for a sample job alert.',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to send test alert.',
        variant: 'destructive'
      });
    }
  };

  const addAlertSkill = () => {
    if (newAlertSkill.trim() && !jobAlertPrefs.skills.includes(newAlertSkill.trim())) {
      setJobAlertPrefs(prev => ({
        ...prev,
        skills: [...prev.skills, newAlertSkill.trim()]
      }));
      setNewAlertSkill('');
    }
  };

  const removeAlertSkill = (skill) => {
    setJobAlertPrefs(prev => ({
      ...prev,
      skills: prev.skills.filter(s => s !== skill)
    }));
  };

  const addAlertLocation = () => {
    if (newAlertLocation.trim() && !jobAlertPrefs.locations.includes(newAlertLocation.trim())) {
      setJobAlertPrefs(prev => ({
        ...prev,
        locations: [...prev.locations, newAlertLocation.trim()]
      }));
      setNewAlertLocation('');
    }
  };

  const removeAlertLocation = (location) => {
    setJobAlertPrefs(prev => ({
      ...prev,
      locations: prev.locations.filter(l => l !== location)
    }));
  };

  const handleExportData = () => {
    toast({
      title: 'Export Started',
      description: 'Your data export will be ready in a few minutes.',
    });
  };

  // Use real stats from API
  const workerStats = {
    appliedJobs: dashboardStats.applied_jobs,
    pendingApps: dashboardStats.pending_applications,
    activeProjects: dashboardStats.active_jobs,
    earnings: dashboardStats.month_earnings,
    rating: dashboardStats.rating || 0,
    reviews: dashboardStats.reviews_count || 0
  };

  // Profile Completeness — 10 weighted fields, each worth 10%
  const completenessFields = [
    { key: 'name', label: 'Full name', filled: !!profileData.name?.trim() },
    { key: 'phone', label: 'Phone number', filled: !!profileData.phone?.trim() },
    { key: 'location', label: 'Location', filled: !!profileData.location?.trim() },
    { key: 'trade', label: 'Primary trade', filled: !!profileData.trade?.trim() },
    { key: 'experience', label: 'Years of experience', filled: !!profileData.experience?.toString().trim() },
    { key: 'hourly_rate', label: 'Hourly rate', filled: !!profileData.hourlyRate?.toString().trim() },
    { key: 'availability', label: 'Availability', filled: !!profileData.availability?.trim() },
    { key: 'bio', label: 'Bio', filled: !!profileData.bio?.trim() && profileData.bio.trim().length >= 30 },
    { key: 'skills', label: 'Skills (≥1)', filled: skills.length > 0 },
    { key: 'languages', label: 'Languages (≥1)', filled: languagesSpoken.length > 0 },
  ];
  const filledCount = completenessFields.filter(f => f.filled).length;
  const profileCompleteness = Math.round((filledCount / completenessFields.length) * 100);
  const missingFields = completenessFields.filter(f => !f.filled);

  const tabs = [
    { id: 'overview', label: t('overview'), icon: ChartBar },
    { id: 'applications', label: t('my_apps_title'), icon: FileText },
    { id: 'portfolio', label: t('portfolio'), icon: Image },
    { id: 'job-alerts', label: t('job_alerts'), icon: Bell },
    { id: 'profile', label: t('tab_profile'), icon: User },
    { id: 'earnings', label: t('tab_earnings'), icon: CurrencyDollar },
    { id: 'security', label: t('security'), icon: ShieldCheck },
    { id: 'settings', label: t('tab_settings'), icon: Gear },
  ];

  // Handle URL tab parameter
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && tabs.some(t => t.id === tab)) {
      setActiveTab(tab);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional mount-only effect
  }, [searchParams]);

  return (
    <>
      <div className="min-h-screen bg-[#F0F0F2] pb-24" data-testid="worker-control-panel">
      <AdminViewBanner role="worker" />
      {/* Simplified Header */}
      <div className="bg-[#2B4C6F] text-white py-6 border-b-4 border-black">
        <div className="max-w-7xl mx-auto px-4 flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <BreadcrumbsInline 
              items={[
                { label: 'Dashboard', href: '/worker-dashboard' },
                { label: 'Settings' }
              ]}
              className="mb-2"
            />
            <h1 className="text-3xl md:text-4xl font-black">{t('control_panel')}</h1>
            <p className="text-blue-100 text-lg mt-1">{t('browse_jobs_sub')}</p>
          </div>
          <div className="flex flex-col items-end gap-2 flex-shrink-0">
            <p className="text-[10px] font-black uppercase tracking-widest text-blue-100/80">{t('language')}</p>
            <LangToggle />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Quick Actions - Big Buttons */}
        <div className="flex flex-wrap gap-3 mb-6">
          <button 
            onClick={() => navigate('/browse-jobs')}
            className="flex items-center gap-2 h-14 px-6 bg-[#2B4C6F] text-white font-bold text-lg rounded-xl border-2 border-black shadow-[3px_3px_0px_#000] hover:shadow-[4px_4px_0px_#000] active:translate-y-1 active:shadow-none transition-all"
            data-testid="quick-action-find-jobs"
          >
            <MagnifyingGlass size={24} weight="bold" />
            Find Jobs
          </button>
          <button 
            onClick={() => navigate('/messages')}
            className="flex items-center gap-2 h-14 px-6 bg-white text-gray-900 font-bold text-lg rounded-xl border-2 border-black shadow-[3px_3px_0px_#000] hover:shadow-[4px_4px_0px_#000] active:translate-y-1 active:shadow-none transition-all"
            data-testid="quick-action-messages"
          >
            <ChatCircle size={24} weight="bold" />
            Messages
          </button>
          <button 
            onClick={() => setActiveTab('applications')}
            className="flex items-center gap-2 h-14 px-6 bg-white text-gray-900 font-bold text-lg rounded-xl border-2 border-black shadow-[3px_3px_0px_#000] hover:shadow-[4px_4px_0px_#000] active:translate-y-1 active:shadow-none transition-all"
            data-testid="quick-action-view-applications"
          >
            <FileText size={24} weight="bold" />
            My Applications
          </button>
        </div>

        {/* Tab Navigation - Horizontal Scroll on Mobile */}
        <div className="bg-white border-2 border-black rounded-xl p-2 mb-6 overflow-x-auto shadow-[4px_4px_0px_#000]">
          <div className="flex gap-2 min-w-max">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  data-testid={`tab-${tab.id}`}
                  className={`flex items-center gap-2 px-4 py-3 rounded-lg font-bold transition-all whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'bg-[#2B4C6F] text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <Icon size={24} weight="bold" />
                  <span className="hidden sm:inline">{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Main Content Area */}
        <div className="space-y-6">
            {activeTab === 'overview' && (
              <OverviewTab
                loadingStats={loadingStats}
                workerStats={workerStats}
                currentPlan={currentPlan}
                setShowPlanComparison={setShowPlanComparison}
                loadingApps={loadingApps}
                applications={applications}
                navigate={navigate}
                setActiveTab={setActiveTab}
                profileCompleteness={profileCompleteness}
                missingFields={missingFields}
              />
            )}

            {activeTab === 'applications' && (
              <ApplicationsTab
                loadingApps={loadingApps}
                applications={applications}
                navigate={navigate}
              />
            )}

            {/* Portfolio Tab */}
            {activeTab === 'portfolio' && (
              <PortfolioTab
                loadingPortfolio={loadingPortfolio}
                portfolioItems={portfolioItems}
                fetchPortfolio={fetchPortfolio}
              />
            )}

            {activeTab === 'job-alerts' && (
              <JobAlertsTab
                jobAlertPrefs={jobAlertPrefs}
                setJobAlertPrefs={setJobAlertPrefs}
                loadingAlerts={loadingAlerts}
                savingAlerts={savingAlerts}
                newAlertSkill={newAlertSkill}
                setNewAlertSkill={setNewAlertSkill}
                addAlertSkill={addAlertSkill}
                removeAlertSkill={removeAlertSkill}
                newAlertLocation={newAlertLocation}
                setNewAlertLocation={setNewAlertLocation}
                addAlertLocation={addAlertLocation}
                removeAlertLocation={removeAlertLocation}
                handleSendTestAlert={handleSendTestAlert}
                handleSaveJobAlerts={handleSaveJobAlerts}
              />
            )}

            {activeTab === 'profile' && (
              <ProfileTab
                profileData={profileData}
                setProfileData={setProfileData}
                skills={skills}
                savingProfile={savingProfile}
                handleSaveProfile={handleSaveProfile}
                languagesSpoken={languagesSpoken}
                setLanguagesSpoken={setLanguagesSpoken}
              />
            )}

            {activeTab === 'earnings' && (
              <EarningsTab workerStats={workerStats} navigate={navigate} />
            )}

            {/* Security Tab */}
            {activeTab === 'security' && <SecurityTab />}

            {activeTab === 'settings' && (
              <SettingsTab
                settings={settings}
                setSettings={setSettings}
                savingSettings={savingSettings}
                handleSaveSettings={handleSaveSettings}
                handleExportData={handleExportData}
              />
            )}
          </div>
        </div>
      </div>

      {/* Plan Comparison Modal */}
      <PlanComparisonModal
        isOpen={showPlanComparison}
        onClose={() => setShowPlanComparison(false)}
        userType="worker"
        currentPlan={currentPlan}
      />
    </>
  );
};

export default WorkerControlPanel;
